import { Flex, Switch } from "@chakra-ui/react-v3";
import type { FilterToggle } from "../../shared-components/fhir-sections/fhir-section";
import { METRIPORT_PRIMARY_FADED, METRIPORT_PURPLE } from "../../shared-logic/style";

export type ToggleFilterProps = {
  filters: FilterToggle[];
  activeFilters: FilterToggle[];
  toggleFilter: (label: string, checked: boolean) => void;
};

/**
 * Get the switch color based on the filter label.
 * Matches the colors used in badge components.
 */
function getSwitchColor(label: string): string {
  switch (label) {
    case "HCC":
      return METRIPORT_PURPLE;
    case "Chronic":
      return METRIPORT_PRIMARY_FADED;
    default:
      return "#3182ce"; // Default blue
  }
}

/**
 * Toggle filter component for filtering data in patient overview tables.
 * Updated version with proper label click handling and matching badge colors.
 *
 * Key fix: Wraps Switch in a label element so clicking the text also toggles the switch.
 */
function ToggleFilter({ filters, activeFilters, toggleFilter }: ToggleFilterProps) {
  return (
    <Flex
      border="solid"
      borderWidth="1px"
      borderRadius="30px"
      borderColor="border.emphasized"
      gap={2}
      px={5}
      py={2}
      alignItems="center"
    >
      {filters.map((filter: FilterToggle) => {
        const isChecked = !!activeFilters.find((f: FilterToggle) => f.label === filter.label);
        const switchColor = getSwitchColor(filter.label);

        return (
          <Switch.Root
            key={filter.label}
            checked={isChecked}
            onCheckedChange={(details: { checked: boolean }) =>
              toggleFilter(filter.label, details.checked)
            }
            size="md"
            fontWeight="medium"
            fontSize="sm"
            cursor="pointer"
            css={{
              "& [data-part='control'][data-state='checked']": {
                backgroundColor: switchColor,
              },
            }}
          >
            <Switch.Label>{filter.label}</Switch.Label>
            <Switch.HiddenInput />
            <Switch.Control />
          </Switch.Root>
        );
      })}
    </Flex>
  );
}

export default ToggleFilter;
